import React, { useRef, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import html2canvas from 'html2canvas';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import ChartGrid from './ChartGrid';
import { useNotification } from '../contexts/NotificationContext';
import { useStyleTemplate } from '../contexts/StyleTemplateContext';
import { getDashboardQueryDataKey, buildParamsForChart } from '../utils/dashboardData';
import { dashboardForDisplay } from '../utils/chartLibrary';
import { waitForReportChartVisuals, REPORT_PDF_CAPTURE_WIDTH } from '../utils/reportChartReady';
import { buildReportPdf } from '../utils/buildReportPdf';
import { resolveReportLogoSrc } from './ReportPdfHeader';
import { specForPreviewApi } from '../utils/specForApiRequest';

const matchDashboardById = (list, id) =>
  list.find(d => d && (d.id === id || Number(d.id) === Number(id)));

const DEFAULT_REPORT_TIMEZONE = 'America/Mexico_City';
/** Ancho fijo de captura (= PDF) para que el grid no recorte tiles a la derecha. */
const PREVIEW_PDF_CONTENT_WIDTH = REPORT_PDF_CAPTURE_WIDTH;

/** Clave en previsualización: misma getDashboardQueryDataKey que usa el dashboard en vivo
 * (incluye la zona horaria), con prefijo dashboardId para no colisionar entre páginas. */
const getPreviewDataKey = (dashboardId, queryId, params, timezone) =>
  `${dashboardId}|${getDashboardQueryDataKey(queryId, params, timezone)}`;

/** Usar estado actual del dashboard cuando es el mismo (mismos filtros y gráficos que ves en la vista principal) */
const mergeWithCurrentDashboard = (fullDashboard, currentDashboard) => {
  if (!fullDashboard || !currentDashboard) return fullDashboard;
  const idMatch = fullDashboard.id != null && currentDashboard.id != null && (Number(fullDashboard.id) === Number(currentDashboard.id) || String(fullDashboard.id) === String(currentDashboard.id));
  if (!idMatch) return fullDashboard;
  return {
    ...fullDashboard,
    charts: (currentDashboard.charts && currentDashboard.charts.length) ? currentDashboard.charts : fullDashboard.charts,
    layout: (currentDashboard.layout && currentDashboard.layout.length) ? currentDashboard.layout : fullDashboard.layout,
    chartLibrary: currentDashboard.chartLibrary ?? fullDashboard.chartLibrary,
    query: currentDashboard.query != null ? currentDashboard.query : fullDashboard.query,
    queryData: currentDashboard.queryData != null ? currentDashboard.queryData : fullDashboard.queryData,
    globalFilterParamNames: (currentDashboard.globalFilterParamNames && currentDashboard.globalFilterParamNames.length) ? currentDashboard.globalFilterParamNames : fullDashboard.globalFilterParamNames,
    globalFilterValues: currentDashboard.globalFilterValues != null ? currentDashboard.globalFilterValues : fullDashboard.globalFilterValues,
    globalFilterParamAliases: currentDashboard.globalFilterParamAliases != null ? currentDashboard.globalFilterParamAliases : fullDashboard.globalFilterParamAliases,
  };
};

const resolveDashboardForReport = (fullDashboard, currentDashboard) =>
  dashboardForDisplay(mergeWithCurrentDashboard(fullDashboard, currentDashboard));

const ReportPreview = ({ open, onClose, report, savedDashboards, getAuthHeaders, currentDashboard = null, isAutomatic = false, scheduleInfo = null }) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const { reportLogoData, reportLogo } = useStyleTemplate();
  const base = axios.defaults.baseURL || '';
  const reportLogoPath = resolveReportLogoSrc({ reportLogoData, reportLogo, baseUrl: base });
  const contentRef = useRef(null);
  const dashboardRefs = React.useRef({});
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false);
  const [queryDataByQueryId, setQueryDataByQueryId] = useState({});
  const [reportDataLoading, setReportDataLoading] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const queryMetaMutation = useMutation({
    mutationFn: ({ queryId, headers, dashboardId }) =>
      axios.get(`/api/queries/${queryId}`, { headers, params: { dashboardId } }),
  });
  const runQueryMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/query', body, { headers }),
  });
  const chartBuilderPreviewMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/preview', body, { headers }),
  });
  const logHistoryMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/reports/history', body, { headers }),
  });

  useEffect(() => {
    if (!open) {
      setPreviewReady(false);
      return;
    }
    if (reportDataLoading) {
      setPreviewReady(false);
      return;
    }
    if (!report?.dashboards?.length) {
      setPreviewReady(true);
      return;
    }

    let cancelled = false;
    const run = async () => {
      await new Promise((r) => setTimeout(r, 150));
      window.dispatchEvent(new Event('resize'));
      await new Promise((r) => setTimeout(r, 350));
      const root = contentRef.current;
      if (root) {
        await waitForReportChartVisuals(root, { timeoutMs: 25000 });
      }
      if (!cancelled) setPreviewReady(true);
    };
    run().catch(() => {
      if (!cancelled) setPreviewReady(true);
    });
    return () => {
      cancelled = true;
      setPreviewReady(false);
    };
  }, [open, reportDataLoading, report?.dashboards, queryDataByQueryId]);

  // Misma lógica que el dashboard: obtener la query por queryId (GET /api/queries/:id) y ejecutarla con parámetros (POST /api/query).
  useEffect(() => {
    if (!open || !report?.dashboards?.length || !savedDashboards?.length) {
      setQueryDataByQueryId({});
      setReportDataLoading(false);
      return;
    }
    setReportDataLoading(true);
    const toFetch = [];
    report.dashboards.forEach((dashboard) => {
      let dashboardId;
      let fullDashboard;
      if (typeof dashboard === 'number' || typeof dashboard === 'string') {
        dashboardId = typeof dashboard === 'string' ? parseInt(dashboard, 10) : dashboard;
        fullDashboard = matchDashboardById(savedDashboards, dashboardId);
      } else if (dashboard && typeof dashboard === 'object') {
        fullDashboard = dashboard;
        dashboardId = dashboard.id;
      } else {
        dashboardId = dashboard?.id;
        fullDashboard = dashboardId ? matchDashboardById(savedDashboards, dashboardId) : null;
      }
      if (!fullDashboard) return;
      toFetch.push({ fullDashboard: resolveDashboardForReport(fullDashboard, currentDashboard) });
    });
    if (toFetch.length === 0) {
      setQueryDataByQueryId({});
      setReportDataLoading(false);
      return;
    }

    const authHeaders = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
    let cancelled = false;

    const run = async () => {
      // 1) Obtener meta (query text, database, connectionId) por (dashboardId, queryId), igual que el
      // dashboard (GET /api/queries/:id). Solo hace falta para graficos SIN visualizationSpec: los del
      // editor de graficos ya traen su propio connectionId/database/spec en el objeto del chart, igual
      // que usa App.js en runQueryWithParams. Se manda dashboardId para que el servidor resuelva queries
      // de dashboards con acceso concedido (no solo propios); sin esto, responden 404 aunque el usuario
      // si pueda verlas.
      const metaByQueryId = {};
      const queryRefs = [];
      const seenQueryRefs = new Set();
      toFetch.forEach(({ fullDashboard }) => {
        (fullDashboard.charts || []).forEach((ch) => {
          if (ch.queryId == null || ch.visualizationSpec) return;
          const refKey = `${fullDashboard.id}|${ch.queryId}`;
          if (seenQueryRefs.has(refKey)) return;
          seenQueryRefs.add(refKey);
          queryRefs.push({ dashboardId: fullDashboard.id, queryId: ch.queryId });
        });
      });
      await Promise.all(
        queryRefs.map(async ({ dashboardId, queryId }) => {
          if (cancelled) return;
          try {
            const res = await queryMetaMutation.mutateAsync({ queryId, headers: authHeaders, dashboardId });
            if (cancelled) return;
            if (res.data?.success && res.data.query && (res.data.query.query != null || res.data.query.queryData)) {
              const q = res.data.query;
              const meta = {
                queryText: q.query || (q.queryData ? null : ''),
                database: q.database,
                connectionId: q.connectionId,
              };
              if (q.queryData && (q.queryData.columns || q.queryData.data)) {
                meta.queryData = { columns: q.queryData.columns || [], data: q.queryData.data || [] };
              }
              metaByQueryId[queryId] = meta;
            }
          } catch (e) {
            if (!cancelled) console.warn('ReportPreview: error al cargar query', queryId, e?.response?.data?.error || e.message);
          }
        })
      );

      // 2) Construir requests por (dashboardId, queryId, params). Graficos con visualizationSpec van al
      // pipeline del chart-builder (igual que el dashboard en vivo); el resto sigue usando el SQL guardado.
      const requests = {};
      const opts = (fullDashboard) => ({
        globalFilterValues: fullDashboard.globalFilterValues || {},
        globalFilterParamAliases: fullDashboard.globalFilterParamAliases || {},
        globalFilterParamNames: fullDashboard.globalFilterParamNames || [],
        getDynamicFilterValue: (ch, paramName) => {
          const list = Array.isArray(ch?.advancedConfig?.dynamicFilters)
            ? ch.advancedConfig.dynamicFilters
            : (ch?.advancedConfig?.dynamicFilter ? [ch.advancedConfig.dynamicFilter] : []);
          const found = list.find((f) => String(f?.paramName || '').trim() === String(paramName || '').trim());
          return found?.selectedValue;
        },
      });

      toFetch.forEach(({ fullDashboard }) => {
        const id = fullDashboard.id;
        const dashboardQuery = fullDashboard.query || '';
        const dashboardDb = fullDashboard.database || fullDashboard.databaseName || '';
        const timezone = fullDashboard.timezone || DEFAULT_REPORT_TIMEZONE;

        (fullDashboard.charts || []).forEach((ch) => {
          if (ch.queryId == null) return;
          const params = buildParamsForChart(ch, opts(fullDashboard));
          // __dashboard_tz siempre resuelto de antemano: sin esto se trataba como parametro
          // faltante y el grafico caia al dato guardado en vez de re-ejecutarse.
          const mergedParams = { ...params, __dashboard_tz: timezone };
          const key = getPreviewDataKey(id, ch.queryId, params, timezone);
          if (requests[key] != null) return;

          if (ch.visualizationSpec) {
            requests[key] = {
              type: 'chartBuilder',
              connectionId: ch.connectionId || fullDashboard.connectionId,
              database: ch.database || dashboardDb,
              spec: ch.visualizationSpec,
              timezone,
              parameters: mergedParams,
            };
            return;
          }

          const meta = metaByQueryId[ch.queryId];
          const queryText = (meta?.queryText != null && meta.queryText !== '') ? meta.queryText : dashboardQuery;
          const db = (meta?.database != null && meta.database !== '') ? meta.database : dashboardDb;

          if (!queryText || !queryText.trim()) {
            // Sin texto de query resuelto (ej. la query no se pudo cargar): igual hay que definir la clave con
            // datos vacíos, si no el gráfico se queda esperando para siempre en vez de mostrar "No hay datos".
            requests[key] = { useSavedData: true, savedData: meta?.queryData || { columns: [], data: [] } };
            return;
          }

          const paramNamesInQuery = (queryText.match(/@([a-zA-Z_][a-zA-Z0-9_]*)/g) || []).map((p) => p.slice(1));
          const uniqueParams = [...new Set(paramNamesInQuery)];
          const missingParams = uniqueParams.filter((p) => mergedParams[p] === undefined || mergedParams[p] === null || String(mergedParams[p]).trim() === '');
          if (uniqueParams.length > 0 && missingParams.length > 0) {
            requests[key] = { useSavedData: true, savedData: meta?.queryData || { columns: [], data: [] } };
            return;
          }
          const fullQuery = /^\s*USE\s+/i.test(queryText.trim()) ? queryText : (db ? `USE [${db}];\n${queryText}` : queryText);
          requests[key] = {
            type: 'rawQuery',
            query: fullQuery,
            database: db,
            connectionId: meta?.connectionId,
            timezone,
            params: uniqueParams.length > 0 ? mergedParams : {},
          };
        });
      });

      // 3) Ejecutar cada request (chart-builder/preview o /api/query segun corresponda) y guardar por clave
      const byKey = {};
      const runOne = async (key, spec) => {
        if (cancelled) return;
        const setByKey = (data) => { byKey[key] = data; };
        if (spec.useSavedData && spec.savedData) {
          setByKey(spec.savedData);
          return;
        }
        try {
          if (spec.type === 'chartBuilder') {
            const res = await chartBuilderPreviewMutation.mutateAsync({
              body: {
                connectionId: spec.connectionId,
                database: spec.database,
                spec: specForPreviewApi(spec.spec),
                timezone: spec.timezone,
                parameters: spec.parameters,
              },
              headers: authHeaders,
            });
            if (cancelled) return;
            if (res.data?.success) {
              setByKey({
                columns: res.data.columns || [],
                data: res.data.data || [],
                ...(res.data.kpiComparison != null ? { kpiComparison: res.data.kpiComparison } : {}),
              });
            } else {
              setByKey({ columns: [], data: [] });
            }
            return;
          }
          const body = {
            query: spec.query,
            database: spec.database,
            connectionId: spec.connectionId,
            timezone: spec.timezone || DEFAULT_REPORT_TIMEZONE,
          };
          if (spec.params && Object.keys(spec.params).length > 0) body.parameters = spec.params;
          const res = await runQueryMutation.mutateAsync({ body, headers: authHeaders });
          if (cancelled) return;
          if (res.data?.success) {
            setByKey({ columns: res.data.columns || [], data: res.data.data || [] });
          } else {
            setByKey({ columns: [], data: [] });
          }
        } catch (err) {
          if (!cancelled) {
            console.warn('Error al cargar datos para previsualización:', key, err?.response?.data?.error || err.message);
            setByKey({ columns: [], data: [] });
          }
        }
      };

      await Promise.all(Object.entries(requests).map(([key, spec]) => runOne(key, spec)));
      if (cancelled) return;
      setQueryDataByQueryId(byKey);
      setReportDataLoading(false);
    };

    run().catch(() => {
      if (!cancelled) setReportDataLoading(false);
    });
    return () => { cancelled = true; setReportDataLoading(false); };
  }, [open, report?.id, report?.dashboards, savedDashboards, currentDashboard, getAuthHeaders]);

  if (!report) return null;

  const logReportHistory = async ({ fileName = null, error = null } = {}) => {
    if (typeof getAuthHeaders !== 'function') return;
    try {
      await logHistoryMutation.mutateAsync({
        body: {
          reportId: report.id ?? null,
          reportName: report.name,
          type: 'manual',
          scheduleInfo: scheduleInfo || null,
          fileName,
          error,
        },
        headers: getAuthHeaders(),
      });
    } catch (err) {
      console.warn('No se pudo registrar historial de reporte:', err?.response?.data?.error || err.message);
    }
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dashboards = report.dashboards || [];
      if (dashboards.length === 0) {
        showWarning('No hay dashboards para generar el PDF');
        return;
      }

      const matchDashboardById = (list, id) =>
        list.find((d) => d && (d.id === id || Number(d.id) === Number(id)));

      const dashboardCaptures = [];
      for (let i = 0; i < dashboards.length; i++) {
        let dashboard;
        let dashboardId;
        if (typeof dashboards[i] === 'number' || typeof dashboards[i] === 'string') {
          dashboardId = typeof dashboards[i] === 'string' ? parseInt(dashboards[i], 10) : dashboards[i];
          dashboard = matchDashboardById(savedDashboards, dashboardId);
        } else if (dashboards[i] && typeof dashboards[i] === 'object') {
          dashboard = dashboards[i];
          dashboardId = dashboard.id;
        } else {
          dashboardId = dashboards[i]?.id;
          dashboard = dashboardId ? matchDashboardById(savedDashboards, dashboardId) : null;
        }
        if (!dashboard) continue;
        const id = dashboard.id;
        dashboardCaptures.push({
          name: dashboard.name,
          getElement: () => dashboardRefs.current[id],
        });
      }

      const perDashboardMode =
        report.schedule?.pdfMode === 'perDashboard' && dashboardCaptures.length > 0;

      if (perDashboardMode) {
        const savedNames = [];
        for (let i = 0; i < dashboardCaptures.length; i += 1) {
          const cap = dashboardCaptures[i];
          const { pdf, fileName } = await buildReportPdf({
            report: {
              ...report,
              name: cap.name ? `${report.name} - ${cap.name}` : report.name,
            },
            reportLogoPath,
            dashboardCaptures: [cap],
            html2canvas,
          });
          pdf.save(fileName);
          savedNames.push(fileName);
          if (i < dashboardCaptures.length - 1) {
            await new Promise((r) => setTimeout(r, 400));
          }
        }
        // No se persiste fileName: pdf.save() descarga el archivo directo al navegador, nunca llega
        // al servidor (generated-reports), asi que no hay nada que el historial pueda ofrecer para
        // descargar despues. Guardarlo igual mostraria un boton de descarga que siempre da 404.
        await logReportHistory({ fileName: null, error: null });
        showSuccess(
          savedNames.length === 1
            ? 'PDF generado correctamente'
            : `${savedNames.length} PDF generados (uno por dashboard)`
        );
      } else {
        const { pdf, fileName } = await buildReportPdf({
          report,
          reportLogoPath,
          dashboardCaptures,
          html2canvas,
        });

        pdf.save(fileName);
        // Mismo motivo que arriba: este PDF solo existe en la descarga del navegador.
        await logReportHistory({ fileName: null, error: null });
        showSuccess('PDF generado correctamente');
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
      await logReportHistory({ error: error.message || 'Error al generar PDF' });
      showError('Error al generar el PDF. Por favor, intenta de nuevo.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Previsualización: {report.name}
      </DialogTitle>
      <DialogContent dividers sx={{ position: 'relative', overflowX: 'auto' }}>
        {reportDataLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              zIndex: 10,
            }}
          >
            <CircularProgress size={48} />
          </Box>
        )}
        <Box
          ref={contentRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            py: 2,
            width: PREVIEW_PDF_CONTENT_WIDTH,
            maxWidth: 'none',
            boxSizing: 'border-box',
          }}
        >
          {report.dashboards && report.dashboards.length > 0 ? (
            report.dashboards
              .filter((d) => {
                if (d == null) return false;
                if (typeof d === 'number' && !Number.isNaN(d)) return true;
                if (typeof d === 'string' && d.trim() !== '') return true;
                if (typeof d === 'object' && d.id != null && d.id !== '') return true;
                return false;
              })
              .map((dashboard, index) => {
              // Si dashboard es un ID (número), buscar el objeto completo en savedDashboards
              // Si dashboard es un objeto, usarlo directamente
              let fullDashboard;
              let dashboardId;
              
              const matchDashboardById = (list, id) =>
                list.find(d => d && (d.id === id || Number(d.id) === Number(id)));
              if (typeof dashboard === 'number' || typeof dashboard === 'string') {
                // Es un ID, buscar en savedDashboards (id puede ser number o string desde API)
                dashboardId = typeof dashboard === 'string' ? parseInt(dashboard, 10) : dashboard;
                fullDashboard = dashboardId != null && !Number.isNaN(dashboardId) ? matchDashboardById(savedDashboards, dashboardId) : null;
              } else if (dashboard && typeof dashboard === 'object') {
                // Es un objeto completo
                fullDashboard = dashboard;
                dashboardId = dashboard.id;
              } else {
                // Fallback: intentar buscar por ID si existe
                dashboardId = dashboard?.id;
                fullDashboard = dashboardId != null && dashboardId !== '' ? matchDashboardById(savedDashboards, dashboardId) : null;
              }
              fullDashboard = fullDashboard
                ? dashboardForDisplay(mergeWithCurrentDashboard(fullDashboard, currentDashboard))
                : null;

              if (!fullDashboard) {
                console.warn('Dashboard no encontrado en ReportPreview:', {
                  dashboardId,
                  dashboard,
                  savedDashboardsIds: savedDashboards.map(d => d?.id),
                  savedDashboardsCount: savedDashboards.length
                });
                return (
                  <Box key={dashboardId ?? index} sx={{ p: 2, backgroundColor: '#fff3cd', borderRadius: 1, mb: 2 }}>
                    <Typography variant="h6" color="warning.main">
                      Dashboard no encontrado (ID: {dashboardId != null && dashboardId !== '' ? dashboardId : 'inválido'})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      El dashboard no está disponible en la lista de dashboards guardados.
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box 
                  key={dashboardId || index}
                  sx={{ 
                    backgroundColor: '#ffffff',
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="h5" gutterBottom>
                    {index + 1}. {fullDashboard.name || 'Dashboard sin nombre'}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    ref={(el) => {
                      if (el && dashboardId) {
                        dashboardRefs.current[dashboardId] = el;
                      }
                    }}
                    data-report-dashboard-capture="true"
                    sx={{
                      minHeight: '400px',
                      width: '100%',
                      overflow: 'visible',
                    }}
                  >
                    <ChartGrid
                      printCapture
                      dashboardTimezone={fullDashboard.timezone || DEFAULT_REPORT_TIMEZONE}
                      queryData={fullDashboard.queryData}
                      queryDataByQueryId={queryDataByQueryId}
                      getQueryDataKey={(qid, p) => getPreviewDataKey(fullDashboard.id, qid, p, fullDashboard.timezone || DEFAULT_REPORT_TIMEZONE)}
                      getAuthHeaders={getAuthHeaders}
                      globalFilterParamNames={fullDashboard.globalFilterParamNames || []}
                      globalFilterValues={fullDashboard.globalFilterValues || {}}
                      globalFilterParamAliases={fullDashboard.globalFilterParamAliases || {}}
                      charts={fullDashboard.charts || []}
                      onChartsChange={() => {}}
                      query={fullDashboard.query || ''}
                      onQueryExecute={() => {}}
                      selectedDatabase={fullDashboard.database || ''}
                      savedQueries={[]}
                      selectedQueryId={null}
                      onQuerySelect={() => {}}
                      layout={fullDashboard.layout || []}
                      onLayoutChange={() => {}}
                      dashboardName={fullDashboard.name || 'Dashboard sin nombre'}
                    />
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Este reporte no tiene dashboards configurados.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          onClick={handleDownloadPDF}
          variant="contained"
          startIcon={isGeneratingPDF ? <CircularProgress size={20} /> : <DownloadIcon />}
          disabled={
            isGeneratingPDF ||
            reportDataLoading ||
            !previewReady ||
            !report.dashboards ||
            report.dashboards.length === 0
          }
        >
          {isGeneratingPDF
            ? 'Generando PDF...'
            : reportDataLoading || !previewReady
              ? 'Cargando preview...'
              : report.schedule?.pdfMode === 'perDashboard' && (report.dashboards?.length || 0) > 1
                ? 'Descargar PDFs (uno por dashboard)'
                : 'Descargar PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportPreview;
